import React, {useEffect, useRef, useState} from "react";
import {useLazyCommand} from "@soap/modules";
import {CenterSpinner} from "@soap/baseweb";
import {ImageBox, useImageFromBlobMeta} from "./image-from-blob-meta";
import {timingModeKeys, timingModes} from "../../../z50-domaincomp/constants/timing-mode-keys";
import {useFormatTimerString} from "../../../hooks/useFormatTimerString";
import {Button} from "../../ui/button";
import {Textarea} from "../../ui/textarea";
import {Input} from "../../ui/input";
import {Checkbox} from "../../ui/checkbox";
import {Select, SelectTrigger, SelectValue, SelectContent, SelectItem} from "../../ui/select";

const INLINE_OPTION_THRESHOLD = 5;

function RadioAnswer({kvPairs, setAnswer}) {
    const [value, setValue] = useState("");

    useEffect(() => {
        if (value) {
            setAnswer(value);
        }
    }, [value, setAnswer]);

    return (
        <div className="space-y-3">
            {kvPairs.map(x => (
                <label
                    key={x.key}
                    htmlFor={`radio-${x.key}`}
                    className={`
                        flex items-start space-x-4 p-4 rounded-lg border-2 cursor-pointer transition-all
                        ${value === x.key 
                            ? 'border-primary bg-primary/5 shadow-sm' 
                            : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                        }
                    `}
                >
                    <input
                        type="radio"
                        id={`radio-${x.key}`}
                        value={x.key}
                        checked={value === x.key}
                        onChange={(e) => {
                            if (e.target.checked) {
                                setValue(x.key);
                            }
                        }}
                        className="mt-1 h-5 w-5 text-primary border-gray-300 focus:ring-2 focus:ring-primary focus:ring-offset-2 cursor-pointer"
                    />
                    <span className="flex-1 text-base text-gray-900 leading-relaxed">
                        {x.value}
                    </span>
                </label>
            ))}
        </div>
    );
}

function CheckboxAnswer({kvPairs, setAnswer}) {
    const [checkedKeys, setCheckedKeys] = useState([]);

    useEffect(() => {
        setAnswer(checkedKeys.join('|'));
    }, [checkedKeys, setAnswer]);

    const handleCheckedChange = (key, checked) => {
        if (checked) {
            setCheckedKeys(prev => [...prev, key]);
        } else {
            setCheckedKeys(prev => prev.filter(k => k !== key));
        }
    };

    return (
        <div className="space-y-3">
            <p className="text-sm text-gray-500 mb-1">(tick all that apply)</p>
            <div className="space-y-3">
                {kvPairs.map(x => {
                    const isChecked = checkedKeys.includes(x.key);
                    return (
                        <label
                            key={x.key}
                            htmlFor={`checkbox-${x.key}`}
                            className={`
                                flex items-start space-x-4 p-4 rounded-lg border-2 cursor-pointer transition-all
                                ${isChecked 
                                    ? 'border-primary bg-primary/5 shadow-sm' 
                                    : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                                }
                            `}
                        >
                            <Checkbox
                                checked={isChecked}
                                onCheckedChange={(checked) => handleCheckedChange(x.key, checked)}
                                id={`checkbox-${x.key}`}
                                className="mt-1 h-5 w-5"
                            />
                            <span className="flex-1 text-base text-gray-900 leading-relaxed">
                                {x.value}
                            </span>
                        </label>
                    );
                })}
            </div>
        </div>
    );
}

function TextAreaAnswer({setAnswer, maxLength}) {
    const [value, setValue] = useState("");

    useEffect(() => {
        setAnswer(value);
    }, [value, setAnswer]);

    return (
        <Textarea
            value={value}
            onChange={(e) => {
                const newValue = e.target.value;
                if (!maxLength || newValue.length <= maxLength) {
                    setValue(newValue);
                }
            }}
            placeholder="Answer Here"
            maxLength={maxLength}
            className="min-h-[140px] text-base border-2 resize-none"
        />
    );
}

function TextBoxAnswer({setAnswer, maxLength}) {
    const [value, setValue] = useState("");

    useEffect(() => {
        setAnswer(value);
    }, [value, setAnswer]);

    return (
        <Input
            value={value}
            onChange={(e) => {
                const newValue = e.target.value;
                if (!maxLength || newValue.length <= maxLength) {
                    setValue(newValue);
                }
            }}
            placeholder="Answer Here"
            maxLength={maxLength}
            className="h-12 text-base border-2"
        />
    );
}

function SelectAnswer({kvPairs, setAnswer}) {
    const [value, setValue] = useState("");

    useEffect(() => {
        if (value) {
            setAnswer(value);
        }
    }, [value, setAnswer]);

    // Find the selected option's display value
    const selectedOption = kvPairs.find(x => x.key === value);
    const displayValue = selectedOption ? selectedOption.value : "";

    return (
        <Select value={value} onValueChange={setValue}>
            <SelectTrigger className="w-full h-12 text-base border-2">
                {displayValue || <span className="text-muted-foreground">Select an answer...</span>}
            </SelectTrigger>
            <SelectContent>
                {kvPairs.map(x => (
                    <SelectItem key={x.key} value={x.key} className="text-base py-3">
                        {x.value}
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
}

function TimingHeader({timingModeKey, questionNumber, questionCount, timeLeft, sectionTitle}) {
    const {fromMilliseconds} = useFormatTimerString();
        
    switch (timingModeKey) {
        case timingModeKeys.NotTimed:
            return (
                <div className="text-center mb-4">
                    <p className="text-base text-gray-700">Question {questionNumber} of {questionCount}</p>
                </div>
            );
        case timingModeKeys.PerSection:
            return (
                <div className="text-center mb-4 space-y-2">
                    <p className="text-base text-gray-700">
                        Time Remaining in {timingModes[timingModeKey].replace('Per ', '')} {sectionTitle}
                    </p>
                    <p className="text-2xl font-semibold text-gray-900">{fromMilliseconds(timeLeft)}</p>
                    <p className="text-base text-gray-700">Question {questionNumber} of {questionCount}</p>
                </div>
            );
        case timingModeKeys.PerQuestion:
            return (
                <div className="text-center mb-4 space-y-2">
                    <p className="text-base text-gray-700">Time Remaining for this Question</p>
                    <p className="text-3xl font-bold text-gray-900">{fromMilliseconds(timeLeft)}</p>
                </div>
            );
        case timingModeKeys.PerAssessment:
            return (
                <div className="text-center mb-4 space-y-2">
                    <p className="text-base text-gray-700">Time Remaining in Assessment</p>
                    <p className="text-4xl font-bold text-gray-900">{fromMilliseconds(timeLeft)}</p>
                    <p className="text-base text-gray-700">Question {questionNumber} of {questionCount}</p>
                </div>
            );
        default:
            return null;
    }
}

function QuestionForm({questionType, maxLength, setAnswer, kvPairs, questionId}) {
    const useRadio = ["boolean"];
    const useTextArea = ["freetext"];
    const useTextBox = ["specific-string"];
    const useCheckBoxes = ["multiple-choice"];
    
    let ctrl;
    if (useRadio.includes(questionType)) {
        ctrl = (<RadioAnswer kvPairs={kvPairs} key={questionId} setAnswer={setAnswer}/>);
    }
    if (questionType === "single-selection") {
        if (kvPairs.length < INLINE_OPTION_THRESHOLD) {
            ctrl = (<RadioAnswer kvPairs={kvPairs} key={questionId} setAnswer={setAnswer}/>);
        } else {
            ctrl = (<SelectAnswer kvPairs={kvPairs} key={questionId} setAnswer={setAnswer}/>);
        }
    }
    if (useTextArea.includes(questionType)) {
        ctrl = (<TextAreaAnswer setAnswer={setAnswer} key={questionId} maxLength={maxLength}/>);
    }
    if (useTextBox.includes(questionType)) {
        ctrl = (<TextBoxAnswer setAnswer={setAnswer} key={questionId} maxLength={maxLength}/>);
    }
    if (useCheckBoxes.includes(questionType)) {
        ctrl = (<CheckboxAnswer kvPairs={kvPairs} key={questionId} setAnswer={setAnswer}/>);
    }
    return ctrl;
}

export function AnswerQuestion({e173, timeLeft, timerControls, dimensions = {maxWidth: 400, maxHeight: 768}}) {
    const viewStates = {
        loading: 1,
        showQuestion: 2
    };

    const [viewState, setViewState] = useState(viewStates.loading);
    
    const {start, pause, resume, reset} = timerControls;
    
    const [sendAnswer] = useLazyCommand('C215v1_SendAnswer');
    const [handleExpiredTimer] = useLazyCommand('C216v1_HandleAssessmentTimerExpired');
    const [skipQuestion] = useLazyCommand('C224v1_SkipQuestion');
    
    const [blobMeta, setBlobMeta] = useState();
    const [answer, setAnswer] = useState();
    const isSubmittingRef = useRef(false);
    
    const enrichedBlob = useImageFromBlobMeta(blobMeta, e173?.e173_NextQuestionMedia?.e173_SasUrl);
    
    useEffect(() => {
        if (timeLeft === 0 && e173 && viewState === viewStates.showQuestion && !isSubmittingRef.current) {
            const {
                e173_NextQuestionId: questionId,
                e173_AssessmentResultId: assessmentResultId,
                e173_TimingModeKey: timingModeKey
            } = e173;
            // Only handle timer expiration for timed assessments
            if (timingModeKey !== timingModeKeys.NotTimed) {
                handleExpiredTimer({
                    C216_AssessmentResultId: assessmentResultId,
                    C216_QuestionId: questionId
                });
            }
        }
    }, [timeLeft, e173, viewState, viewStates.showQuestion, handleExpiredTimer])
    
    useEffect(() => {
        if (e173 && viewState === viewStates.loading) {
            // Reset submission state for new question
            isSubmittingRef.current = false;
            
            const {
                e173_TimeInSeconds: timeInSeconds,
                e173_TimingModeKey: timingModeKey,
                e173_IsFirstQuestionInSection: firstQuestionInSection,
            } = e173;

            const questionHasImage = e173.e173_NextQuestionMedia?.e173_BlobMeta;

            if (questionHasImage && !enrichedBlob) {
                setBlobMeta(questionHasImage);
                return;
            }

            const isTimed = timingModeKey !== timingModeKeys.NotTimed;
            const shouldStartTimer = (timingModeKey === timingModeKeys.PerQuestion ||
                (timingModeKey === timingModeKeys.PerSection && firstQuestionInSection));
            const shouldResumeTimer = (
                (timingModeKey === timingModeKeys.PerSection && !firstQuestionInSection) ||
                (timingModeKey === timingModeKeys.PerAssessment)  // Always resume for per-assessment
            );
                
            if (isTimed) {
                if (shouldStartTimer) {
                    start(timeInSeconds * 1000);
                }
                if (shouldResumeTimer)
                {
                    resume();
                }
            }
            
            setViewState(viewStates.showQuestion);
        }
    }, [e173, enrichedBlob, viewState, viewStates.loading, start, resume])

    switch (viewState) {
        case viewStates.loading:
            return <CenterSpinner/>;
        case viewStates.showQuestion:

            const {
                e173_NextQuestionPossibleAnswers: kvPairs,
                e173_NextQuestionId: questionId,
                e173_NextQuestionTypeKey: questionType,
                e173_QuestionNumber: questionNumber,
                e173_QuestionCount: questionCount,
                e173_SectionTitle: sectionTitle,
                e173_AssessmentResultId: assessmentResultId,
                e173_TimingModeKey: timingModeKey,
                e173_IsLastQuestionInTest: isLastQuestion,
                e173_MaxLength: maxLength
            } = e173;

            const caption = e173.e173_NextQuestionMedia?.e173_Caption;
            let questionText = e173.e173_NextQuestionText;
            if (!questionText.endsWith("?")) questionText += "?";
            let buttonText = isLastQuestion ? "Finish" : "Continue";

            return (
                <div className="flex flex-col items-center w-full max-w-2xl mx-auto space-y-6">
                    <TimingHeader 
                        timingModeKey={timingModeKey} 
                        questionNumber={questionNumber} 
                        questionCount={questionCount} 
                        timeLeft={timeLeft} 
                        sectionTitle={sectionTitle} 
                    />
                    
                    <div className="w-full space-y-4">
                        <div className="flex justify-center">
                            <ImageBox 
                                key={questionId} 
                                enrichedBlob={enrichedBlob} 
                                caption={caption ? <p className="text-sm text-gray-600 text-center mt-2">{caption}</p> : null}
                            />
                        </div>
                        
                        <div className="text-center">
                            <h2 className="text-xl font-semibold text-gray-900">{questionText}</h2>
                        </div>
                        
                        <div className="border-t border-gray-200 pt-6">
                            <div className="flex justify-center w-full">
                                <div className="w-full max-w-lg">
                                    <QuestionForm 
                                        questionId={questionId} 
                                        questionType={questionType} 
                                        maxLength={maxLength} 
                                        kvPairs={kvPairs} 
                                        setAnswer={setAnswer} 
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div className="flex justify-end w-full pt-4 gap-3">
                        {!isLastQuestion && (
                            <Button
                                variant="outline"
                                disabled={isSubmittingRef.current}
                                onClick={() => {
                                    isSubmittingRef.current = true;
                                    // Pause timer for all timed modes during skip to prevent race conditions
                                    if (timingModeKey !== timingModeKeys.NotTimed) {
                                        pause();
                                    }
                                    skipQuestion({
                                        C224_AssessmentResultId: assessmentResultId,
                                        C224_QuestionId: questionId,
                                    });
                                }}
                                className="min-w-[120px]"
                            >
                                Skip
                            </Button>
                        )}
                        <Button
                            disabled={!answer || isSubmittingRef.current}
                            onClick={() => {
                                isSubmittingRef.current = true;
                                // Pause timer for all timed modes during submission to prevent race conditions
                                if (timingModeKey !== timingModeKeys.NotTimed) {
                                    pause();
                                }
                                sendAnswer({
                                    C215_AssessmentResultId: assessmentResultId,
                                    C215_QuestionId: questionId,
                                    C215_AnswerAsString: answer,
                                }); 
                            }}
                            className="min-w-[120px]"
                        >
                            {buttonText}
                        </Button>
                    </div>
                </div>
            );
    }
}

